import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuFlagTriangleRight, LuFlaskConical } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  PROJECT_COLUMNS,
  daysUntilDue,
  displayValue,
  formatIsoDate,
  getFilterValueLabel,
  initialsOf,
  isCurrencyFixedFlag,
  isDueSoon,
  isEmptyValue,
  isFinishedFlag,
  isTestProjectFlag,
  projectStatusOf,
} from '../projectColumns';

/** "12 gün kaldı" / "Bugün bitiyor" / "3 gün geçti" for the end date; null without a date. */
export const useDueLabel = () => {
  const { t } = useTranslation();
  return (project) => {
    const days = daysUntilDue(project);
    if (days === null) return null;
    if (days === 0) return t('projectManagement.due.today');
    return days > 0 ? t('projectManagement.due.daysLeft', { count: days }) : t('projectManagement.due.overdue', { count: -days });
  };
};

/** Plain-text form of a cell, for its tooltip. */
export const getCellTitle = (project, key) => {
  const value = project[key];
  if (key === 'projectStatus' || isEmptyValue(value)) return undefined;
  return String(value);
};

/** Aktif / Pasif. */
export const StatusBadge = ({ project }) => {
  const { t } = useTranslation();
  const active = projectStatusOf(project) === 'Aktif';
  return (
    <Badge tone={active ? 'success' : 'neutral'}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${active ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
      {active ? t('projectManagement.status.active') : t('projectManagement.status.inactive')}
    </Badge>
  );
};

StatusBadge.propTypes = { project: PropTypes.object.isRequired };

/** Small "Bitti" / "Test" markers next to the project title. */
export const ProjectFlags = ({ project, compact }) => {
  const { t } = useTranslation();
  const finished = isFinishedFlag(project);
  const test = isTestProjectFlag(project);
  if (!finished && !test) return null;
  const pill = 'tw-inline-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold';
  return (
    <>
      {finished && (
        <span title={t('projectManagement.badges.finished')} className={`${pill} tw-bg-teal-500/15 tw-text-teal-700 dark:tw-text-teal-300`}>
          <LuFlagTriangleRight aria-hidden="true" className="tw-h-3 tw-w-3" />
          {compact ? <span className="tw-sr-only">{t('projectManagement.badges.finished')}</span> : t('projectManagement.badges.finished')}
        </span>
      )}
      {test && (
        <span title={t('projectManagement.badges.test')} className={`${pill} tw-bg-violet-500/15 tw-text-violet-700 dark:tw-text-violet-300`}>
          <LuFlaskConical aria-hidden="true" className="tw-h-3 tw-w-3" />
          {compact ? <span className="tw-sr-only">{t('projectManagement.badges.test')}</span> : t('projectManagement.badges.test')}
        </span>
      )}
    </>
  );
};

ProjectFlags.propTypes = { project: PropTypes.object.isRequired, compact: PropTypes.bool };

/** Content of one project table cell. */
const ProjectCell = ({ columnKey, project }) => {
  const { t, locale } = useTranslation();
  const dueLabel = useDueLabel();
  const value = project[columnKey];
  const column = PROJECT_COLUMNS[columnKey];
  const muted = <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;

  if (columnKey === 'customer') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span
          aria-hidden="true"
          className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400/90 tw-to-violet-600/90 tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
        >
          {initialsOf(value, '?')}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{displayValue(value)}</span>
      </span>
    );
  }
  if (columnKey === 'titleShort') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{displayValue(value)}</span>
        <ProjectFlags project={project} compact />
      </span>
    );
  }
  if (columnKey === 'projectStatus') return <StatusBadge project={project} />;
  if (isEmptyValue(value)) return muted;
  if (columnKey === 'projectPhase') {
    return <Badge tone="info" className="tw-max-w-full"><span className="tw-truncate">{getFilterValueLabel(t, columnKey, value)}</span></Badge>;
  }
  if (columnKey === 'projectDesign') return <span className="tw-block tw-truncate">{getFilterValueLabel(t, columnKey, value)}</span>;
  if (columnKey === 'isCurrencyFixed') {
    return isCurrencyFixedFlag(project)
      ? <span className="tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{t('projectManagement.yes')}</span>
      : <span className="tw-text-slate-500 dark:tw-text-slate-400">{t('projectManagement.no')}</span>;
  }
  if (columnKey === 'projectDueDate') {
    const soon = isDueSoon(project);
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5" title={dueLabel(project) || undefined}>
        <span className="tw-truncate tw-tabular-nums">{formatIsoDate(value, locale)}</span>
        {soon && <LuCalendarClock aria-label={dueLabel(project)} className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-amber-500" />}
      </span>
    );
  }
  if (column.type === 'date') return <span className="tw-block tw-truncate tw-tabular-nums">{formatIsoDate(value, locale)}</span>;
  return <span className="tw-block tw-truncate">{String(value)}</span>;
};

ProjectCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  project: PropTypes.object.isRequired,
};

/** One row of the card list (narrow screens). */
export const ProjectCardRow = ({ project }) => {
  const { t, locale } = useTranslation();
  const dueLabel = useDueLabel();
  const phase = isEmptyValue(project.projectPhase) ? '' : getFilterValueLabel(t, 'projectPhase', project.projectPhase);
  const customer = isEmptyValue(project.customer) ? '' : String(project.customer);
  const soon = isDueSoon(project);

  return (
    <>
      <span
        aria-hidden="true"
        className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-violet-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
      >
        {initialsOf(project.titleShort || project.customer)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
          <span className="tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{displayValue(project.titleShort)}</span>
          <ProjectFlags project={project} compact />
        </span>
        {(customer || phase) && (
          <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
            {[customer, phase].filter(Boolean).join(' · ')}
          </span>
        )}
        {!isEmptyValue(project.projectDueDate) && (
          <span className={`tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-tabular-nums ${soon ? 'tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>
            <LuCalendarClock aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
            {formatIsoDate(project.projectDueDate, locale)}
            {soon && dueLabel(project) && <span className="tw-inline"> · {dueLabel(project)}</span>}
          </span>
        )}
      </span>
      <span className="tw-shrink-0">
        <StatusBadge project={project} />
      </span>
    </>
  );
};

ProjectCardRow.propTypes = { project: PropTypes.object.isRequired };

export default ProjectCell;
